import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  IndicationByIndicationFlow,
  IndicationFlowContextData,
  ProposalByIndicationFlow,
  ContractByIndicationFlow,
  CustomerByIndicationFlow,
} from "./types";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useGetProposalAndIndicationByIdQuery,
  useCustomerGetQuery,
} from "~/graphql/types";
import { toast } from "react-toastify";

const IndicationFlowContext = createContext({} as IndicationFlowContextData);

export const IndicationFlowProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialStep = Number(searchParams.get("step") || 0);
  const [stepper, setStepperState] = useState(initialStep);

  const setStepper = useCallback(
    (value: React.SetStateAction<number>) => {
      setStepperState((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        return next;
      });
      // Atualiza o query param separadamente, fora do state updater
      setSearchParams(
        (params) => {
          const currentStep = Number(params.get("step") || 0);
          const next = typeof value === "function" ? value(currentStep) : value;
          params.set("step", String(next));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [selectedIndication, setSelectedIndication] =
    useState<null | IndicationByIndicationFlow>(null);
  const [selectedProposal, setSelectedProposal] =
    useState<null | ProposalByIndicationFlow>(null);
  const [selectedContract, setSelectedContract] =
    useState<null | ContractByIndicationFlow>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<null | CustomerByIndicationFlow>(null);
  const isPf = selectedProposal?.customerRef.personType === "pf";

  const {
    loading: getIndcationAndProposalIsLoading,
    refetch: refetchProposalAndIndication,
  } = useGetProposalAndIndicationByIdQuery({
    variables: {
      id: id || "",
    },
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data.getIndicationById._id) {
        setSelectedIndication(data.getIndicationById);
      }
      if (data.getIndicationRelatedEntitiesById.proposal?._id) {
        setSelectedProposal(data.getIndicationRelatedEntitiesById.proposal);
      }
      if (data.getIndicationRelatedEntitiesById.contract?._id) {
        setSelectedContract(data.getIndicationRelatedEntitiesById.contract);
      }
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const { loading: getCustomerIsLoading } = useCustomerGetQuery({
    variables: {
      id: selectedContract?.customerId || "",
    },
    skip: !selectedContract?.customerId,
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data.customerGet?._id) {
        setSelectedCustomer(data.customerGet);
      }
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const goToPage = useCallback(
    (page: number) => {
      setStepper(page);
    },
    [setStepper],
  );

  return (
    <IndicationFlowContext.Provider
      value={{
        setStepper,
        stepper,
        goToPage,
        getIndcationAndProposalIsLoading,
        selectedIndication,
        selectedProposal,
        selectedContract,
        selectedCustomer,
        setSelectedIndication,
        setSelectedProposal,
        setSelectedContract,
        setSelectedCustomer,
        isPf,
        getCustomerIsLoading,
        refetchProposalAndIndication,
      }}
    >
      {children}
    </IndicationFlowContext.Provider>
  );
};

export const useIndicationFlow = () => useContext(IndicationFlowContext);
